import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE } from "@/components/Reveal";
import { BookCallButton } from "@/components/Ctas";
import SystemsDiagram from "@/components/SystemsDiagram";
import { Link } from "react-router-dom";
import { ArrowDown } from "lucide-react";

const LINES = [
  <>Design your business<br className="hidden sm:block" /> like a product.</>,
  <>Run it like a system.</>,
  <>Scale it with <em className="italic text-amber">AI</em>.</>,
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const diagramY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 30]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-[72px]"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-20 pt-16 sm:px-6 sm:pt-24 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-28 lg:pt-28">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.3em] text-amber"
            data-testid="hero-eyebrow"
          >
            <span className="inline-block h-px w-10 bg-amber" aria-hidden="true" />
            KP Strategy
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-7 font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl lg:text-7xl"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, ease: EASE, delay: 0.2 + i * 0.14 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.75 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-body sm:text-lg"
          >
            KP Strategy helps founders, service businesses and nonprofits use
            Product Management thinking to turn strategy into better products,
            programs, ways of working and AI-powered operating systems.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-6"
          >
            <BookCallButton testid="hero-cta-primary" />
            <Link
              data-testid="hero-cta-secondary"
              to="/how-it-works"
              className="group inline-flex items-center gap-2 border-b border-ink/30 pb-1 text-sm font-semibold tracking-wide text-ink transition-colors duration-300 hover:border-amber hover:text-amber"
            >
              See How It Works
              <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: diagramY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.5 }}
          className="lg:col-span-5"
        >
          <SystemsDiagram className="mx-auto w-full max-w-[440px]" />
        </motion.div>
      </div>
    </section>
  );
}
